import { useQueryClient } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import { Members } from '@air/api';
import { type MemberWithEmail } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getWorkspaceMembersKey } from '~/swr-hooks/members/useGetWorkspaceMembers';
import { getWorkspaceTotalsKey } from '~/swr-hooks/workspaces/useWorkspaceTotals';
import { saveTeamInviteNudgeInviteTriggered } from '~/utils/teamInviteNudge';

export const useAddWorkspaceMembers = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const addWorkspaceMember = async (newMembers: MemberWithEmail[]) => {
    try {
      const workspaceId = currentWorkspace?.id;
      invariant(workspaceId, 'No workspace id');
      const { members } = await Members.add({ workspaceId, members: newMembers });
      saveTeamInviteNudgeInviteTriggered({ workspaceId });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getWorkspaceMembersKey(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: getWorkspaceTotalsKey(workspaceId) }),
      ]);

      return members;
    } catch (error) {
      throw reportErrorToBugsnag({
        error,
        context: 'Failed to add workspace members',
        metadata: {
          data: {
            members: JSON.stringify(newMembers),
          },
        },
      });
    }
  };

  return {
    addWorkspaceMember,
  };
};
